import { useLayoutEffect, useMemo, useRef } from "react";
import type { ViewBlock } from "@/lib/chat-render";
import { sum } from "@/lib/sum";
import {
	clearTranscriptHighlights,
	collectMatchRanges,
	paintTranscriptMatches,
} from "@/lib/transcript-highlight";
import {
	countMatchesBefore,
	countTranscriptMatches,
	locateTranscriptMatch,
} from "@/lib/transcript-search";
import { useTranscriptSearchStore } from "@/stores/useTranscriptSearchStore";
import { stableBlockId } from "./chat-scroll-anchor";

/** ChatView が描いているブロックに付ける目印。値はブロック番号 (生成中の応答は blocks.length)。 */
const SEARCH_INDEX_ATTRIBUTE = "data-search-index";

/** 一致箇所を画面中央へ寄せる。Range 自身は寄せられないため、一致を含む要素を使う。 */
function scrollRangeIntoView(range: Range | undefined): void {
	range?.startContainer.parentElement?.scrollIntoView({ block: "center" });
}

/**
 * 履歴検索の一致をデータから数えてストアへ渡し、描かれているブロックの一致を塗る。
 * いま辿っている一致のブロック番号を返す。画面外でも描かせるため、仮想スクロールの描画範囲へ足すこと。
 */
export function useTranscriptSearch(
	blocks: readonly ViewBlock[],
	streamingText: string,
): number | null {
	const open = useTranscriptSearchStore((s) => s.open);
	const query = useTranscriptSearchStore((s) => s.query);
	const activeIndex = useTranscriptSearchStore((s) => s.activeIndex);
	const setMatchCount = useTranscriptSearchStore((s) => s.setMatchCount);

	const counts = useMemo(
		() => (open ? countTranscriptMatches(blocks, streamingText, query) : []),
		[open, blocks, streamingText, query],
	);
	const active = locateTranscriptMatch(counts, activeIndex);

	// 古い履歴が先頭へ足されると一致の番号が後ろへずれるため、足された分だけ送って同じ箇所を保つ。
	const firstBlockIdRef = useRef<string | null>(null);
	useLayoutEffect(() => {
		const previousFirstId = firstBlockIdRef.current;
		firstBlockIdRef.current = blocks[0] ? stableBlockId(blocks[0]) : null;
		const previousFirstIndex =
			previousFirstId === null
				? -1
				: blocks.findIndex((block) => stableBlockId(block) === previousFirstId);
		const insertedBefore =
			previousFirstIndex > 0 ? countMatchesBefore(counts, previousFirstIndex) : 0;
		setMatchCount(
			sum(counts, ({ count }) => count),
			insertedBefore,
		);
	}, [blocks, counts, setMatchCount]);

	// 描かれているブロックはスクロールのたびに入れ替わるため、描画ごとに塗り直す。
	// 描画と同じフレームで塗り、入れ替わったブロックに古いハイライトが一瞬残らないようにする。
	const activeRangeRef = useRef<Range | undefined>(undefined);
	useLayoutEffect(() => {
		if (!open || query.length === 0) return;
		const others: Range[] = [];
		let activeRange: Range | undefined;
		for (const element of document.querySelectorAll(
			`[data-transcript] [${SEARCH_INDEX_ATTRIBUTE}]`,
		)) {
			const body = element.querySelector("[data-message-body]");
			if (body === null) continue;
			const ranges = collectMatchRanges(body, query);
			if (Number(element.getAttribute(SEARCH_INDEX_ATTRIBUTE)) === active?.index) {
				// データと DOM で件数がずれても、そのブロックのどこかを辿れるよう範囲内に収める。
				[activeRange] = ranges.splice(Math.min(active.occurrence, ranges.length - 1), 1);
			}
			others.push(...ranges);
		}
		activeRangeRef.current = activeRange;
		paintTranscriptMatches(others, activeRange);
		return clearTranscriptHighlights;
	});

	// 移動操作と検索語の変更のときだけ一致箇所へ寄せる。本文の伸長でスクロールを奪わない。
	// activeRangeRef は上のエフェクト (同じコミット内で先に走る) が更新済み。
	useLayoutEffect(() => {
		scrollRangeIntoView(activeRangeRef.current);
	}, [activeIndex, query]);

	return active?.index ?? null;
}
